<script setup lang="ts">
import Dialog from 'primevue/dialog';
import { useEntryStore } from '@/stores/entry';
import { watch } from 'vue';
import { DATE_FORMAT_3, formattedDate } from '@/utils';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  }
});

const entryStore = useEntryStore();

const emits = defineEmits(['onClose', 'onSave']);

const closeDialog = async () => {
  emits('onClose');
};

watch(
  () => props.visible,
  (newValue: boolean) => {
    if (newValue) {
      entryStore.fetchEntrySignHistory();
    }
  }
);
</script>
<template>
  <Dialog :visible="visible"
    modal
    header="Signature History"
    class="dialog"
    style="width: 800px"
    :closeOnEscape="true"
    :draggable="false"
    @update:visible="closeDialog">
    Here's the list of signatures for this entry:

    <div class="activity-table">
      <table>
        <thead>
          <tr>
            <th>Time</th>
            <th>User</th>
            <th>Activity</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(activity, index) in entryStore.entryActivities"
            :key="index">
            <td>{{ formattedDate(activity.createdAt, DATE_FORMAT_3) }}</td>
            <td>{{ activity.activityOwner.name }}</td>
            <td>
              <div class="entry-created"
                v-if="activity.subType === 'RESPONSE_CREATE'">
                Entry Created by {{ activity.activityOwner.name }}
              </div>
              <div class="entry-created"
                v-if="
                  activity.subType === 'RESPONSE_UPDATE' &&
                  activity.difference.newValues?.reasons.type === 'update'
                ">
                Entry Updated by {{ activity.activityOwner.name }}
              </div>
              <div class="entry-created"
                v-if="activity.subType !== 'RESPONSE_CREATE' && activity.difference.newValues?.reasons.type === 'review'">
                Entry Reviewd by {{ activity.activityOwner.name }}
              </div>
            </td>
            <td>
              {{ activity.difference.newValues?.reasons.reason }}
              {{
                activity.difference.newValues?.reasons.comment
                  ? ` (${activity.difference.newValues?.reasons.comment})`
                  : ""
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </Dialog>
</template>

<style lang="scss" scoped>
.activity-table {
  margin-top: 16px;
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  font-family: "Segoe UI", sans-serif;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    color: #333;

    thead {
      background-color: #f4f6f8;

      th {
        text-align: left;
        padding: 12px 16px;
        font-weight: 600;
        color: #555;
      }
    }

    tbody {
      tr {
        border-top: 1px solid #eee;
        transition: background 0.2s;

        &:hover {
          background-color: #f9fafb;
        }

        td {
          padding: 12px 16px;
        }
      }
    }
  }
}
</style>
